import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material';
import React, {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react';
import { getCssVar } from '../utils/themeColors';

// Extend the theme's palette interface to include custom colors
declare module '@mui/material/styles' {
  interface Palette {
    purple: Palette['primary'];
  }

  interface PaletteOptions {
    purple?: PaletteOptions['primary'];
  }
}

type ThemeMode = 'light' | 'dark';

interface ThemeContextType {
  mode: ThemeMode;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

// Function to create MUI theme based on current CSS variables
const createAppTheme = (mode: ThemeMode) => {
  // Apply theme to document
  document.documentElement.setAttribute('data-theme', mode);

  // Create MUI theme with dynamic CSS variables
  return createTheme({
    palette: {
      mode,
      background: {
        default: getCssVar('--background-default'),
        paper: getCssVar('--background-paper'),
      },
      action: {
        hover: getCssVar('--action-hover'),
        selected: getCssVar('--action-selected'),
        disabled: getCssVar('--action-disabled'),
        disabledBackground: getCssVar('--action-disabled-background'),
      },
      primary: {
        main: getCssVar('--primary-main'),
        light: getCssVar('--primary-light'),
        dark: getCssVar('--primary-dark'),
        contrastText: getCssVar('--primary-contrast-text'),
      },
      secondary: {
        main: getCssVar('--secondary-main'),
        light: getCssVar('--secondary-light'),
        dark: getCssVar('--secondary-dark'),
        contrastText: getCssVar('--secondary-contrast-text'),
      },
      success: {
        main: getCssVar('--success-main'),
        light: getCssVar('--success-light'),
        dark: getCssVar('--success-dark'),
        contrastText: getCssVar('--success-contrast-text'),
      },
      error: {
        main: getCssVar('--error-main'),
        light: getCssVar('--error-light'),
        dark: getCssVar('--error-dark'),
        contrastText: getCssVar('--error-contrast-text'),
      },
      warning: {
        main: getCssVar('--warning-main'),
        light: getCssVar('--warning-light'),
        dark: getCssVar('--warning-dark'),
        contrastText: getCssVar('--warning-contrast-text'),
      },
      info: {
        main: getCssVar('--info-main'),
        light: getCssVar('--info-light'),
        dark: getCssVar('--info-dark'),
        contrastText: getCssVar('--info-contrast-text'),
      },
      grey: {
        50: getCssVar('--grey-50'),
        100: getCssVar('--grey-100'),
        200: getCssVar('--grey-200'),
        300: getCssVar('--grey-300'),
        400: getCssVar('--grey-400'),
        500: getCssVar('--grey-500'),
        600: getCssVar('--grey-600'),
        700: getCssVar('--grey-700'),
        800: getCssVar('--grey-800'),
        900: getCssVar('--grey-900'),
      },
      text: {
        primary: getCssVar('--text-primary'),
        secondary: getCssVar('--text-secondary'),
        disabled: getCssVar('--text-disabled'),
      },
      divider: getCssVar('--divider'),
      // Custom color for QC checks
      purple: {
        main: getCssVar('--purple-main'),
        light: getCssVar('--purple-light'),
        dark: getCssVar('--purple-dark'),
        contrastText: getCssVar('--purple-contrast-text'),
      },
    },
    components: {
      MuiContainer: {
        defaultProps: {
          disableGutters: true,
          maxWidth: false,
        },
        styleOverrides: {
          root: {
            height: '100%',
          },
        },
      },
      MuiTypography: {
        styleOverrides: {
          h4: {
            textAlign: 'left',
            marginBottom: '20px',
            fontWeight: 'normal',
          },
        },
      },
      MuiButton: {
        defaultProps: {
          disableRipple: true,
        },
        styleOverrides: {
          root: {
            minHeight: '30px',
            textTransform: 'none',
            padding: '0px 15px',
            borderRadius: '5px',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
          },
        },
      },
      MuiTextField: {
        defaultProps: {
          size: 'small',
          fullWidth: true,
        },
        styleOverrides: {
          root: {
            backgroundColor: getCssVar('--input-background'),
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: getCssVar('--input-border'),
              },
              '&:hover fieldset': {
                borderColor: getCssVar('--input-border-hover'),
              },
              '&.Mui-focused fieldset': {
                borderColor: getCssVar('--input-border-focus'),
              },
              '&.Mui-error fieldset': {
                borderColor: getCssVar('--input-border-error'),
              },
            },
          },
        },
      },
      MuiTableCell: {
        defaultProps: {
          height: '50px',
        },
        styleOverrides: {
          root: {
            padding: '5px',
            borderBottom: `1px solid ${getCssVar('--table-border')}`,
          },
          head: {
            backgroundColor: getCssVar('--table-header-bg'),
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            '&:hover': {
              backgroundColor: getCssVar('--table-row-hover'),
            },
            '&.Mui-selected': {
              backgroundColor: getCssVar('--table-row-selected'),
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundColor: getCssVar('--background-paper'),
            backgroundImage: 'none',
          },
          elevation1: {
            backgroundColor: getCssVar('--background-elevation-1'),
            boxShadow: getCssVar('--shadow-1'),
          },
          elevation2: {
            backgroundColor: getCssVar('--background-elevation-2'),
            boxShadow: getCssVar('--shadow-2'),
          },
          elevation3: {
            backgroundColor: getCssVar('--background-elevation-3'),
            boxShadow: getCssVar('--shadow-3'),
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundColor: getCssVar('--background-paper'),
            borderColor: getCssVar('--border-primary'),
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: getCssVar('--divider'),
          },
        },
      },
    },
  });
};

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [mode, setMode] = useState<ThemeMode>(() => {
    // Temporarily disable dark mode

    // // Check localStorage for saved preference
    // const savedMode = localStorage.getItem('theme-mode') as ThemeMode;
    // if (savedMode) return savedMode;

    // // Check system preference
    // if (
    //   window.matchMedia &&
    //   window.matchMedia('(prefers-color-scheme: dark)').matches
    // ) {
    //   return 'dark';
    // }

    return 'light';
  });

  const [theme, setTheme] = useState(() => createAppTheme(mode));

  useEffect(() => {
    // Save preference to localStorage
    localStorage.setItem('theme-mode', mode);

    // Update theme when mode changes
    setTheme(createAppTheme(mode));
  }, [mode]);

  const toggleTheme = () => {
    setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
  };

  return (
    <ThemeContext.Provider value={{ mode, toggleTheme }}>
      <MuiThemeProvider theme={theme}>{children}</MuiThemeProvider>
    </ThemeContext.Provider>
  );
};
